import { useState } from 'react';

import { useCreateCustomer, useCreateDeal, useCustomers, useShows } from '../../api/hooks';
import type { OrgUserView } from '../../api/types';
import {
  Button,
  DateInput,
  FormField,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';

const NEW_CUSTOMER_OPTION = '__new-customer';

interface DealModalProps {
  /** Sales exec options — empty when the viewer cannot list org users. */
  salesExecs: OrgUserView[];
  onClose: () => void;
}

/** New Deal form: customer (with inline create), show, amount, exec, close. */
export function DealModal({ salesExecs, onClose }: DealModalProps) {
  const { showToast } = useToast();
  const customers = useCustomers({ limit: 100 });
  const shows = useShows({ limit: 100 });
  const createCustomer = useCreateCustomer();
  const createDeal = useCreateDeal();

  const [customerChoice, setCustomerChoice] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [showId, setShowId] = useState('');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [salesExecId, setSalesExecId] = useState('');
  const [expectedClose, setExpectedClose] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  async function handleCreate() {
    if (title.trim() === '') {
      showToast('Deal title is required', 'error');
      return;
    }

    if (
      customerChoice === '' ||
      (customerChoice === NEW_CUSTOMER_OPTION && newCustomerName.trim() === '')
    ) {
      showToast('Choose or create a customer', 'error');
      return;
    }

    setIsSaving(true);

    try {
      const customerId =
        customerChoice === NEW_CUSTOMER_OPTION
          ? (await createCustomer.mutateAsync({ name: newCustomerName.trim() })).id
          : customerChoice;

      await createDeal.mutateAsync({
        customerId,
        title: title.trim(),
        ...(showId !== '' ? { showId } : {}),
        ...(amount.trim() !== '' ? { amount: Number(amount) } : {}),
        ...(salesExecId !== '' ? { salesExecId } : {}),
        ...(expectedClose !== '' ? { expectedClose } : {}),
      });
      showToast('Deal created');
      onClose();
    } catch (error) {
      showToast(errorMessage(error, 'Could not create deal'), 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      actions={
        <>
          <Button disabled={isSaving} onClick={handleCreate} type="button">
            {isSaving ? 'Creating…' : 'Create Deal'}
          </Button>
          <Button onClick={onClose} type="button" variant="secondary">
            Cancel
          </Button>
        </>
      }
      title="New Deal"
    >
      <div className="rfp-modal-wide rfp-form-grid">
        <FormField htmlFor="deal-customer" label="Customer">
          <Select
            id="deal-customer"
            onChange={(event) => setCustomerChoice(event.target.value)}
            value={customerChoice}
          >
            <option value="">Select a customer…</option>
            {(customers.data?.data ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
            <option value={NEW_CUSTOMER_OPTION}>+ Create new customer…</option>
          </Select>
        </FormField>

        {customerChoice === NEW_CUSTOMER_OPTION && (
          <FormField htmlFor="deal-new-customer" label="New Customer Name">
            <TextInput
              id="deal-new-customer"
              onChange={(event) => setNewCustomerName(event.target.value)}
              value={newCustomerName}
            />
          </FormField>
        )}

        <FormField htmlFor="deal-show" label="Show (optional)">
          <Select id="deal-show" onChange={(event) => setShowId(event.target.value)} value={showId}>
            <option value="">No show</option>
            {(shows.data?.data ?? []).map((show) => (
              <option key={show.id} value={show.id}>
                {show.name}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField htmlFor="deal-title" label="Title">
          <TextInput
            id="deal-title"
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g. Harbour Lights S2 — Nordics SVOD"
            value={title}
          />
        </FormField>

        <FormField htmlFor="deal-amount" label="Amount £">
          <TextInput
            id="deal-amount"
            min="0"
            onChange={(event) => setAmount(event.target.value)}
            step="0.01"
            type="number"
            value={amount}
          />
        </FormField>

        <FormField htmlFor="deal-exec" label="Sales Executive">
          <Select
            id="deal-exec"
            onChange={(event) => setSalesExecId(event.target.value)}
            value={salesExecId}
          >
            <option value="">Unassigned</option>
            {salesExecs.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField htmlFor="deal-close" label="Expected Close">
          <DateInput
            id="deal-close"
            onChange={(event) => setExpectedClose(event.target.value)}
            value={expectedClose}
          />
        </FormField>
      </div>
    </Modal>
  );
}
